import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { z } from "zod";
import {
  MiAutocomplete,
  MiButtonGroup,
  MiIcon,
  MiIconButton,
  MiInputSelectBox,
  MiInputTextbox,
} from "@miplatform/components";
import { numberSymbolsRegex } from "@miplatform/constants";
import { useFeatureFlags } from "@miplatform/feature-flags";
import { formatOpenBalanceDisplay } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  mdiCheck,
  mdiCurrencyUsd,
  mdiPencilOutline,
  mdiPercentOutline,
  mdiTrashCanOutline,
  mdiWindowClose,
} from "@mdi/js";
import { Box, CircularProgress, Tooltip, useTheme } from "@mui/material";
import {
  DISCOUNT_TYPE,
  PREBILL_ITEM_TYPE,
  PREBILL_ITEM_TYPE_OPTIONS,
  PREBILL_UNCATEGORIZED_TYPE,
} from "./prebillConstants";

const stringPriceSchema = z
  .string()
  .min(1, "Invalid.")
  .refine((val) => {
    const num = parseFloat(val.replace(numberSymbolsRegex, ""));

    return !Number.isNaN(num) && num >= 0;
  });

const numberPriceSchema = z.number().min(0, "Invalid.");

const prebillItemSchema = z
  .object({
    type: z
      .string()
      .min(1, "Type is required")
      .refine(
        (data) => data !== PREBILL_UNCATEGORIZED_TYPE,
        "Type is required"
      ),
    name: z.string().min(3, "Minimum length is 3 characters"),
    quantity: z.string().min(1, "Invalid"),
    unitPrice: z.union([numberPriceSchema, stringPriceSchema]),
    isDiscount: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.isDiscount && data.quantity) {
      const num = Number(data.quantity);
      if (Number.isNaN(num) || num <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Invalid",
          path: ["quantity"],
        });
      }
    }
  });

export const PrebillItem = ({
  isDeleteDisabled,
  item = {},
  laborRates,
  materialOptions = [],
  total = 0,
  onUpdate,
  onDelete,
}) => {
  const theme = useTheme();
  const { featureFlags } = useFeatureFlags();
  const isMiDayFeatureEnabled = featureFlags?.miDayEnabled;
  const [isEditing, setIsEditing] = useState(item?.isNew);
  const [discountType, setDiscountType] = useState(
    item.isPercentage ? DISCOUNT_TYPE.PERCENTAGE : DISCOUNT_TYPE.AMOUNT
  );
  const isSubmitting = !!item.isSubmitting;
  const materialItemOptions = useMemo(() => {
    if (
      !item.name ||
      materialOptions.some((material) => material.value === item.name)
    ) {
      return materialOptions;
    }

    return [{ value: item.name, label: item.name }, ...materialOptions];
  }, [item.name, materialOptions]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      type: item?.type || PREBILL_ITEM_TYPE.LABOR,
      name: item?.name || "",
      quantity: item?.quantity?.toString() || "1",
      unitPrice: item.isNew ? "" : Math.abs(item?.unitPrice).toString() || "",
      isDiscount: !!item.isDiscount,
    },
    resolver: zodResolver(prebillItemSchema),
  });

  const type = watch("type");
  const quantity = watch("quantity");
  const unitPrice = watch("unitPrice");
  const isDiscount = type === PREBILL_ITEM_TYPE.DISCOUNT;

  const totalValueOfItem = useMemo(() => {
    const currentPrice =
      parseFloat(
        typeof unitPrice === "string"
          ? unitPrice.replace(numberSymbolsRegex, "")
          : unitPrice
      ) || 0;
    const currentQuantity = parseFloat(quantity || 0);

    if (isDiscount) {
      return (
        (discountType === DISCOUNT_TYPE.PERCENTAGE
          ? total * (currentPrice / 100)
          : currentPrice) * -1
      );
    }

    return currentQuantity * currentPrice;
  }, [discountType, isDiscount, quantity, total, unitPrice]);

  const handleSave = async (data) => {
    const updatedItem = {
      ...item,
      ...data,
      quantity: isDiscount ? 1 : parseFloat(data.quantity),
      unitPrice: Math.abs(
        typeof unitPrice === "string"
          ? unitPrice.replace(numberSymbolsRegex, "")
          : unitPrice
      ),
      isPercentage: discountType === DISCOUNT_TYPE.PERCENTAGE && isDiscount,
    };

    const isUpdated = await onUpdate(updatedItem);

    if (isUpdated) {
      setIsEditing(false);
    }
  };

  const handleDelete = () => {
    onDelete(item);
  };

  const handleCancel = () => {
    if (item.isNew) {
      onDelete(item);
      return;
    }

    reset({
      type: item?.type || "",
      name: item.name || "",
      quantity: item.quantity?.toString() || "1",
      unitPrice: Math.abs(item?.unitPrice).toString() || "",
      isDiscount: !!item.isDiscount,
    });

    setIsEditing(false);
  };

  const getIconButtonProps = (onClick, testId) => ({
    onClick,
    "data-testid": testId,
  });

  const renderSubmittingState = () => (
    <Box display="flex" alignItems="center" justifyContent="center" width={80}>
      <CircularProgress size={20} color="primary" />
    </Box>
  );

  const renderEditingControls = () => (
    <>
      <MiIconButton
        {...getIconButtonProps(
          handleSubmit(handleSave),
          "save-prebill-item-btn"
        )}
      >
        <MiIcon color={theme.palette.primary.main} path={mdiCheck} />
      </MiIconButton>
      <MiIconButton
        {...getIconButtonProps(handleCancel, "close-prebill-item-btn")}
      >
        <MiIcon color={theme.palette.red.main} path={mdiWindowClose} />
      </MiIconButton>
    </>
  );

  const renderDefaultControls = () => (
    <>
      <MiIconButton
        {...getIconButtonProps(
          () => setIsEditing(true),
          "edit-prebill-item-btn"
        )}
      >
        <MiIcon color={theme.palette.black.main} path={mdiPencilOutline} />
      </MiIconButton>
      <MiIconButton
        disabled={isDeleteDisabled}
        {...getIconButtonProps(handleDelete, "delete-prebill-item-btn")}
      >
        <MiIcon color={theme.palette.black.main} path={mdiTrashCanOutline} />
      </MiIconButton>
    </>
  );

  const handleLaborRateChange = (e) => {
    const selectedRate = laborRates.find(
      (rate) => rate.value === e.target.value
    );
    if (selectedRate) {
      setValue("name", selectedRate.value);
      setValue("unitPrice", selectedRate.rate.toString());
    }
  };

  const handleMaterialSelection = (value) => {
    const selectedMaterial = materialItemOptions.find(
      (material) => material.value === value
    );

    if (selectedMaterial) {
      setValue("quantity", selectedMaterial.quantity?.toString() || "1");
    }
  };

  const getPriceLabel = () => {
    if (isDiscount) {
      return discountType === DISCOUNT_TYPE.PERCENTAGE
        ? " Percentage"
        : " Amount";
    }

    return "Price";
  };

  const getItemWidth = () => {
    if (isDiscount) {
      return 220;
    }

    return 250;
  };

  return (
    <Box
      display="flex"
      width="100%"
      flexDirection="row"
      justifyContent="space-between"
      gap={2}
      sx={{
        flexWrap: "nowrap",
        maxWidth: "760px",
        backgroundColor: isSubmitting
          ? "rgba(3, 169, 244, 0.05)"
          : "transparent",
        opacity: isSubmitting ? 0.8 : 1,
        transition: "all 0.3s ease",
      }}
    >
      <Box>
        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <MiInputSelectBox
              {...field}
              disableNone
              required
              handleChange={(e) => {
                const { value } = e.target;

                setValue(
                  "name",
                  value === PREBILL_ITEM_TYPE.WARRANTY ? "Warranty Service" : ""
                );
                setValue("quantity", "1");

                if (value === PREBILL_ITEM_TYPE.DISCOUNT) {
                  setDiscountType(DISCOUNT_TYPE.AMOUNT);
                }

                field.onChange(e);

                setValue(
                  "unitPrice",
                  value === PREBILL_ITEM_TYPE.WARRANTY ? "0.00" : ""
                );
              }}
              size="small"
              variant="outlined"
              label="Type"
              error={errors.type}
              helperText={errors.type?.message}
              readOnly={!isEditing || isSubmitting}
              options={PREBILL_ITEM_TYPE_OPTIONS}
              sx={{ width: 120 }}
            />
          )}
        />
      </Box>
      <Box sx={{ flexBasis: getItemWidth(), flexShrink: 0 }}>
        <Controller
          name="name"
          control={control}
          render={({ field }) => {
            const commonProps = {
              ...field,
              required: true,
              size: "small",
              variant: "outlined",
              label: "Item",
              error: errors.name,
              helperText: errors.name?.message,
              fullWidth: true,
              readOnly: !isEditing || isSubmitting,
            };

            if (type === PREBILL_ITEM_TYPE.LABOR) {
              return (
                <MiInputSelectBox
                  {...commonProps}
                  disableNone
                  options={laborRates}
                  handleChange={handleLaborRateChange}
                />
              );
            }

            if (type === PREBILL_ITEM_TYPE.MATERIAL && isMiDayFeatureEnabled) {
              return (
                <MiAutocomplete
                  {...commonProps}
                  freeSolo
                  options={materialItemOptions.map((o) => o.label)}
                  onChange={(_, value) => {
                    field.onChange(value || "");
                    handleMaterialSelection(value);
                  }}
                  onInputChange={(_, inputValue, reason) => {
                    if (reason === "input") {
                      field.onChange(inputValue);
                    }
                  }}
                />
              );
            }

            return <MiInputTextbox {...commonProps} />;
          }}
        />
      </Box>

      {!isDiscount && (
        <Controller
          name="quantity"
          control={control}
          render={({ field }) => (
            <MiInputTextbox
              {...field}
              required
              size="small"
              type="number"
              sx={{ width: 200 }}
              variant="outlined"
              label="Qty"
              error={errors.quantity}
              helperText={errors.quantity?.message}
              readOnly={!isEditing || isSubmitting}
            />
          )}
        />
      )}
      <Box display="flex" gap={2} maxHeight={40} alignItems="center">
        {isDiscount && (
          <MiButtonGroup
            disabled={!isEditing || isSubmitting}
            selected={discountType}
            data={[
              {
                icon: mdiCurrencyUsd,
                "data-testid": "amount-discount-btn",
                iconColor:
                  discountType === DISCOUNT_TYPE.AMOUNT
                    ? theme.palette.primary.main
                    : theme.palette.grey.main,
                value: DISCOUNT_TYPE.AMOUNT,
                sx: { width: 40 },
              },
              {
                icon: mdiPercentOutline,
                "data-testid": "percentage-discount-btn",
                iconColor:
                  discountType === DISCOUNT_TYPE.PERCENTAGE
                    ? theme.palette.primary.main
                    : theme.palette.grey.main,
                value: DISCOUNT_TYPE.PERCENTAGE,
                sx: { width: 40 },
              },
            ]}
            containerStyle={{
              border: `1px solid ${theme.palette.grey.darkAccent}`,
            }}
            handleClick={(item) => {
              setDiscountType(item.value);
              if (item.value === DISCOUNT_TYPE.PERCENTAGE) {
                setValue(
                  "unitPrice",
                  Math.min(
                    100,
                    parseFloat(unitPrice.replace(numberSymbolsRegex, "") || 0)
                  ).toString()
                );
              }
            }}
          />
        )}
        <Box height={40}>
          <Controller
            name="unitPrice"
            control={control}
            render={({ field }) => (
              <NumericFormat
                fixedDecimalScale
                thousandSeparator
                customInput={MiInputTextbox}
                decimalScale={2}
                allowNegative={false}
                suffix={
                  discountType === DISCOUNT_TYPE.PERCENTAGE && isDiscount
                    ? "%"
                    : undefined
                }
                prefix={
                  (discountType === DISCOUNT_TYPE.AMOUNT || !isDiscount) &&
                  discountType !== DISCOUNT_TYPE.PERCENTAGE
                    ? "$"
                    : undefined
                }
                isAllowed={(values) => {
                  const { floatValue } = values;
                  if (floatValue === undefined) return true;

                  if (floatValue < 0) return false;

                  if (discountType === DISCOUNT_TYPE.PERCENTAGE && isDiscount) {
                    return floatValue <= 100;
                  }

                  return true;
                }}
                {...field}
                shrink
                required
                size="small"
                sx={{ width: 100 }}
                variant="outlined"
                label={getPriceLabel()}
                error={errors.unitPrice}
                helperText={errors.unitPrice?.message}
                readOnly={
                  !isEditing ||
                  isSubmitting ||
                  type === PREBILL_ITEM_TYPE.LABOR ||
                  type === PREBILL_ITEM_TYPE.WARRANTY
                }
                placeholder={
                  discountType === DISCOUNT_TYPE.PERCENTAGE && isDiscount
                    ? "0.00%"
                    : "0.00"
                }
                onValueChange={(values) => {
                  const { floatValue } = values;
                  if (floatValue) {
                    field.onChange(floatValue);
                  }
                }}
                value={field.value || ""}
              />
            )}
          />
        </Box>
        <Box width={100}>
          <Tooltip
            title={formatOpenBalanceDisplay(totalValueOfItem)}
            arrow
            placement="top"
          >
            <Box bgcolor="grey.200" borderRadius={2} border width="100%">
              <MiInputTextbox
                readOnly
                label="Total"
                value={formatOpenBalanceDisplay(totalValueOfItem)}
                placeholder="$0.00"
                fullWidth
                textfieldProps={{
                  sx: {
                    fontSize: "0.875rem",
                    ...(totalValueOfItem < 0 && {
                      color: "error.main",
                    }),
                  },
                }}
                sx={{
                  "& .MuiInputLabel-root.Mui-disabled": {
                    color: "black",
                  },
                  // Input text color when disabled
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "black", // needed for Chrome
                    color: "black", // fallback
                  },
                }}
              />
            </Box>
          </Tooltip>
        </Box>
        <Box display="flex" alignItems="center">
          {(() => {
            if (isSubmitting) return renderSubmittingState();

            if (isEditing) return renderEditingControls();

            return renderDefaultControls();
          })()}
        </Box>
      </Box>
    </Box>
  );
};
